import React, { useState, useEffect } from 'react';
import Pagination from '../../components/Pagination.tsx';
import { GetSubsInfo } from '../../services/Common.ts';
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from '../../theme.ts';

interface Subscription {
  subscription_title: string;
  start_date: string;
  end_date: string;
}

interface UserSubscription {
  id: string;
  name: string;
  email: string;
  subscriptions: Subscription[];
}

const SubscriptionInfo = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [type, setType] = useState('ORGANIZATION')
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(14);

  const reawdata = localStorage.getItem('user');
  let cred = reawdata ? JSON.parse(reawdata) : null;
  let token = cred?.access;

  useEffect(() => {
    // Fetch subscription data (replace with your API call)
    const fetchSubscriptions = async () => {
      setLoading(true)
      try {
        const response: any = await GetSubsInfo(token, type, status)
        setSubscriptions(response.data.users);
        setLoading(false)
        // console.log('response',response.data)
      } catch (err) {
        console.log(err)
        setLoading(false)
      }
    };

    fetchSubscriptions();
  }, [type, status]);

  const filteredSubscriptions = subscriptions?.filter((sub) =>
    sub.name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
    sub.email?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  // Pagination calculations
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedSubscriptions = filteredSubscriptions.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredSubscriptions.length / rowsPerPage);

  const handleCancelSubscription = (id: string) => {
    // Logic to cancel the subscription (e.g., API call)
    console.log(`Cancel subscription for user ID: ${id}`);
    setSubscriptions((prev) => prev.filter((sub) => sub.id !== id));
  };

  return (
    <div style={{ backgroundColor: currentTheme.background }}>
      {/* Search Bar */}
      <div className="mb-1 flex gap-2">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 p-2 rounded-lg border focus:outline-none shadow-sm"
          style={{ borderColor: currentTheme.border, backgroundColor: currentTheme.surface, color: currentTheme.text }}
        />
        <select onChange={(e) => setType(e.target.value)} className="p-2 rounded border" style={{ borderColor: currentTheme.border, backgroundColor: currentTheme.surface, color: currentTheme.text }}>
          <option value="" disabled>Select user Type</option>
          <option value='ORGANIZATION'>organization</option>
          <option value='INFLUENCER'>influencer</option>
        </select>
        <select onChange={(e) => setStatus(e.target.value)} className="p-2 rounded border" style={{ borderColor: currentTheme.border, backgroundColor: currentTheme.surface, color: currentTheme.text }}>
          <option value='' disabled>Filter By Status</option>
          <option value='all'>All</option>
          <option value='active'>Active</option>
          <option value='expired'>Expired</option>
        </select>
      </div>

      {/* Subscription Table */}
      <div className="shadow-lg rounded-xl overflow-hidden" style={{ backgroundColor: currentTheme.surface }}>
        {loading ? (
          <div className="flex justify-center items-center p-6">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4" style={{ borderColor: currentTheme.primary }}></div>
            <p className="ml-4" style={{ color: currentTheme.primary }}>Loading...</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10" style={{ backgroundColor: currentTheme.primary }}>
              <tr>
                <th className="py-2 px-3" style={{ color: currentTheme.inverseText }}>#</th>
                <th className="py-2 px-3" style={{ color: currentTheme.inverseText }}>Name</th>
                <th className="py-2 px-3" style={{ color: currentTheme.inverseText }}>Email</th>
                <th className="py-2 px-3" style={{ color: currentTheme.inverseText }}>Plan</th>
                <th className="py-2 px-3" style={{ color: currentTheme.inverseText }}>Start Date</th>
                <th className="py-2 px-3" style={{ color: currentTheme.inverseText }}>End Date</th>
                <th className="py-2 px-3" style={{ color: currentTheme.inverseText }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSubscriptions.length > 0 ? (
                paginatedSubscriptions.map((sub, index) => (
                  <tr key={sub.id} style={{ borderBottom: `1px solid ${currentTheme.border}` }}>
                    <td className="py-2 px-3" style={{ color: currentTheme.text }}>{startIndex + index + 1}</td>
                    <td className="py-2 px-3" style={{ color: currentTheme.text }}>{sub.name}</td>
                    <td className="py-2 px-3" style={{ color: currentTheme.text }}>{sub.email}</td>
                    <td className="py-2 px-3" style={{ color: currentTheme.text }}>{sub?.subscriptions[0]?.subscription_title}</td>
                    <td className="py-2 px-3" style={{ color: currentTheme.text }}>{sub?.subscriptions[0]?.start_date}</td>
                    <td className="py-2 px-3" style={{ color: currentTheme.text }}>{sub?.subscriptions[0]?.end_date}</td>
                    <td className="py-2 px-3">
                      <button
                        onClick={() => handleCancelSubscription(sub.id)}
                        className="px-3 py-1 rounded transition"
                        style={{ color: currentTheme.surface, backgroundColor: currentTheme.error }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-6" style={{ color: currentTheme.textSecondary }}>
                    No subscriptions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredSubscriptions.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: currentTheme.border }}>
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: currentTheme.textSecondary }}>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 rounded border text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: currentTheme.surface,
                color: currentTheme.text,
                borderColor: currentTheme.border
              }}
            >
              <option value={14}>14</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm" style={{ color: currentTheme.textMuted }}>
              Showing {filteredSubscriptions.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, filteredSubscriptions.length)} of {filteredSubscriptions.length} subscriptions
            </span>
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
};

export default SubscriptionInfo;
